// ピクセル表面の置き場。Vue のリアクティブにも Pinia にも入れない
// モジュールスコープの Map。Document からは pixelId で参照する。
//
// 書き込みは documentStore.mutate() が渡す PixelWriter 経由に限定する
// (履歴にタイルパッチを自動収集するため)。

import { PixelSurface, type TilePatch } from './pixelSurface'
import type { Channels, Tile } from './tile'
import type { Rect } from '@/types/geometry'

export interface SurfacePatch {
  pixelId: string
  /** 生成なら null、破棄なら破棄前の表面 */
  before: PixelSurface | null
  after: PixelSurface | null
}

export interface PixelWriter {
  create(id: string, channels: Channels): PixelSurface
  /** 生成済みの表面 (画像デコード結果など) を登録する */
  adopt(surface: PixelSurface): void
  drop(id: string): void
  surface(id: string): PixelSurface
  writeRect(id: string, rect: Rect, data: Uint8ClampedArray): TilePatch[]
  writeTiles(id: string, patches: Iterable<[string, Tile | null]>): TilePatch[]
}

export interface TransactResult<T> {
  result: T
  tilePatches: TilePatch[]
  surfacePatches: SurfacePatch[]
}

export class PixelStore {
  private map = new Map<string, PixelSurface>()
  private writeDepth = 0

  get(id: string): PixelSurface | undefined {
    return this.map.get(id)
  }

  require(id: string): PixelSurface {
    const s = this.map.get(id)
    if (!s) throw new Error(`ピクセル表面が見つからない: ${id}`)
    return s
  }

  has(id: string): boolean {
    return this.map.has(id)
  }

  ids(): IterableIterator<string> {
    return this.map.keys()
  }

  get size(): number {
    return this.map.size
  }

  /** 全表面の概算バイト数 */
  byteLength(): number {
    let n = 0
    for (const s of this.map.values()) n += s.byteLength()
    return n
  }

  /**
   * 書き込みトランザクション。documentStore.mutate() と復元処理だけが呼ぶ。
   * PixelWriter が発行したパッチを収集して返す。
   */
  transact<T>(fn: (writer: PixelWriter) => T): TransactResult<T> {
    this.writeDepth++
    const tilePatches: TilePatch[] = []
    const surfacePatches: SurfacePatch[] = []
    const store = this
    const writer: PixelWriter = {
      create(id, channels) {
        if (store.map.has(id)) throw new Error(`ピクセル表面が既に存在する: ${id}`)
        const s = new PixelSurface(id, channels)
        store.map.set(id, s)
        surfacePatches.push({ pixelId: id, before: null, after: s })
        return s
      },
      adopt(surface) {
        if (store.map.has(surface.id)) throw new Error(`ピクセル表面が既に存在する: ${surface.id}`)
        store.map.set(surface.id, surface)
        surfacePatches.push({ pixelId: surface.id, before: null, after: surface })
      },
      drop(id) {
        const s = store.map.get(id)
        if (!s) return
        store.map.delete(id)
        surfacePatches.push({ pixelId: id, before: s, after: null })
      },
      surface(id) {
        return store.require(id)
      },
      writeRect(id, rect, data) {
        const p = store.require(id).writeRect(rect, data)
        for (const x of p) tilePatches.push(x)
        return p
      },
      writeTiles(id, patches) {
        const p = store.require(id).writeTiles(patches)
        for (const x of p) tilePatches.push(x)
        return p
      }
    }
    try {
      const result = fn(writer)
      return { result, tilePatches, surfacePatches }
    } catch (e) {
      // 途中まで書いたピクセルを戻してから再スローする (呼び出し側は何も反映しない)
      for (let i = tilePatches.length - 1; i >= 0; i--) this.applyTilePatch(tilePatches[i]!, 'undo')
      for (let i = surfacePatches.length - 1; i >= 0; i--) this.applySurfacePatch(surfacePatches[i]!, 'undo')
      throw e
    } finally {
      this.writeDepth--
    }
  }

  /** undo/redo 専用: 表面の生成/破棄を戻す・やり直す */
  applySurfacePatch(p: SurfacePatch, direction: 'undo' | 'redo'): void {
    const target = direction === 'undo' ? p.before : p.after
    if (target) this.map.set(p.pixelId, target)
    else this.map.delete(p.pixelId)
  }

  /** undo/redo 専用: タイルを戻し、直前まで入っていたタイルを返す (逆方向用に保持する) */
  applyTilePatch(p: TilePatch, direction: 'undo' | 'redo'): Tile | null {
    const s = this.map.get(p.pixelId)
    if (!s) return null
    const target = direction === 'undo' ? p.before : p.after
    const prev = s.getTile(p.key) ?? null
    s.writeTiles([[p.key, target]])
    return prev
  }

  /** 復元・新規作成時に全表面を差し替える。履歴も同時に破棄されること */
  replaceAll(surfaces: Iterable<PixelSurface>): void {
    this.map = new Map()
    for (const s of surfaces) this.map.set(s.id, s)
  }

  /** 文書からも履歴からも参照されなくなった表面を落とす */
  retainOnly(ids: Set<string>): number {
    let dropped = 0
    for (const id of [...this.map.keys()]) {
      if (!ids.has(id)) {
        this.map.delete(id)
        dropped++
      }
    }
    return dropped
  }

  get isWriting(): boolean {
    return this.writeDepth > 0
  }
}

export const pixelStore = new PixelStore()
